import { Alert, Button, Stack, TextInput, Textarea } from '@mantine/core'
import { Form, hasLength, isEmail, useForm } from '@mantine/form'
import { useSetState, useTimeout } from '@mantine/hooks'
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react'
import clsx from 'clsx'
import { useMutation } from '@tanstack/react-query'
import type { ButtonProps } from '@mantine/core'
import { saveContact } from '@/components/contact/serverFn.ts'

export type ContactFormType = {
	name: string
	email: string
	message: string
}
export const ContactForm = ({ variant, btnProps, className }: { variant?: string; btnProps?: ButtonProps; className?: string }) => {
	const [alert, setAlert] = useSetState({
		color: '',
		message: '',
		show: false,
	})
	const { start } = useTimeout(() => setAlert({ show: false }), 10000)
	const form = useForm<ContactFormType>({
		initialValues: {
			name: '',
			email: '',
			message: '',
		},
		validate: {
			email: isEmail('Invalid email'),
			name: hasLength({ min: 1 }, 'Name is required'),
			message: hasLength({ min: 10 }, 'Message must be minimum 10 characters long'),
		},
	})

	const { mutateAsync: save, isPending } = useMutation({
		mutationFn: async (values: ContactFormType) => await saveContact({ data: values }),
		onSuccess: () => {
			setAlert({ color: 'green', message: 'Your message has been sent successfully!', show: true })
			form.reset()
			start()
		},
	})
	return (
		<Form form={form} onSubmit={save} className={clsx('w-full', className)}>
			<Stack className={'px-4 w-full'} gap={'lg'}>
				<TextInput
					name={'name'}
					withAsterisk
					{...form.getInputProps('name')}
					error={form.errors.name}
					placeholder={'Type your name here...'}
					label={'Full Name'}
					variant={variant || 'brand'}
				/>
				<TextInput
					name={'email'}
					withAsterisk
					{...form.getInputProps('email')}
					error={form.errors.email}
					type={'email'}
					placeholder={'Type your email here...'}
					label={'Your Email'}
					variant={variant || 'brand'}
				/>
				<Textarea
					name={'message'}
					withAsterisk
					{...form.getInputProps('message')}
					error={form.errors.message}
					placeholder={'Type your message here...'}
					rows={6}
					label={'Your Message'}
					variant={variant || 'brand'}
				/>
				{alert.show && <Alert icon={alert.color === 'green' ? <IconCheck /> : <IconAlertTriangle />} title={alert.message} color={alert.color} />}
				<Button mt={'sm'} loading={isPending} type={'submit'} variant={'brand'} {...btnProps}>
					Submit Now
				</Button>
			</Stack>
		</Form>
	)
}
